/**
 * The globals helper reads global variables, that were loaded before the app start:
 * main.ts injects the globals and session info loaded from the backend,
 * which then are globally available through the helper functions.
 *
 * (c) Kaden und Partner AG
 */

import { Benutzer } from '@/models/Benutzer';
import { Rolle, ROLES } from '@/models/Rolle';
import { reactive, ref, type Ref } from 'vue';
import { AuthLevel } from './auth';

export interface AppGlobals {
  environment: string;
  version: string;
}

export interface AppSession {
  user: Benutzer | null;
  role: Rolle | null;
  csrfToken: string;
  authLevel: AuthLevel;
}
/** reactive singleton object with global variables, injected on app startup */
const globals = reactive<AppGlobals>({} as AppGlobals);
/** reactive singleton object with session variables, injected on app startup */
const session = reactive<AppSession>({} as AppSession);

/**
 * Takes a raw object with app globals, and sets them in the globals
 * reactive object. Should be set by the app init process, when
 * the globals are loaded from the backend.
 *
 * @param newGlobals
 */
export function setGlobals(newGlobals: AppGlobals) {
  Object.assign(globals, newGlobals);
}

/**
 * Returns a reactive object with the global variables
 */
export function useGlobals(): AppGlobals {
  return globals;
}

/**
 * Takes a raw object with app session data, and sets them in the session
 * reactive object. Should be set by the app init process, when
 * the session data are loaded from the backend.
 *
 * @param newSession
 */
export function setSession(newSession: AppSession) {
  session.user = newSession.user ? new Benutzer().set(newSession.user) : null;
  session.role = newSession.role ? new Rolle().set(newSession.role) : null;
  session?.user?.commit();
  session.csrfToken = newSession.csrfToken ?? null;
  session.authLevel = newSession.authLevel ?? null;
}

/**
 * Returns a reactive object with the session variables
 */
export function useSession(): AppSession {
  return session as AppSession;
}

/**
 * Returns a global variable value from the globals object delivered by auth/init
 * @param {string} key The global var name
 * @returns the value or null
 */
export function globalVar(key: keyof AppGlobals) {
  return useGlobals()[key];
}

/**
 * Returns the authenticated user, or null
 */
export function useAuthUser(): Benutzer | null {
  return session.user as Benutzer | null;
}

export function useAuthUserRole(): Rolle | null {
  return session.role as Rolle | null;
}

export function authUserHasRole(role: string): boolean {
  const userRole = useAuthUserRole();
  return userRole?.get(role) === true;
}

export function authUserHasAnyRole(role: string[]): boolean {
  const userRole = useAuthUserRole();
  let ret = false;
  role.forEach((r) => {
    if (userRole?.get(r) === true) {
      ret = true;
    }
  })
  return ret;
}

export function isAuthenticated(): boolean {
  return useAuthUser() !== null;
}
